/**
 * FrequencyCapCard — Card for a frequency cap configuration
 *
 * Displays cap details: name, jurisdiction, max attempts / period,
 * channel scope, cross-channel indicator, active toggle, and actions.
 */
import { Card, CardContent, Badge, Button } from '@respectly/ui';
import { Switch } from '@respectly/ui';
import { Pencil, Trash2, BookOpen } from 'lucide-react';
import type { FrequencyCapConfig } from '@respectly/shared-types';

// ============================================
// Types
// ============================================

interface FrequencyCapCardProps {
  cap: FrequencyCapConfig;
  onEdit: (cap: FrequencyCapConfig) => void;
  onDelete?: (cap: FrequencyCapConfig) => void;
  onToggle?: (cap: FrequencyCapConfig) => void;
}

// ============================================
// Helpers
// ============================================

function formatPeriod(periodHours: number): string {
  if (periodHours < 24) return `${periodHours} hour${periodHours !== 1 ? 's' : ''}`;
  const days = Math.round(periodHours / 24);
  return `${days} day${days !== 1 ? 's' : ''}`;
}

// ============================================
// Component
// ============================================

export function FrequencyCapCard({
  cap,
  onEdit,
  onDelete,
  onToggle,
}: FrequencyCapCardProps) {
  return (
    <Card className="border-slate-200/60 dark:border-slate-700/60">
      <CardContent className="p-4 space-y-3">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-sm text-slate-900 dark:text-white">
              {cap.name}
            </span>
            <Badge variant="outline" className="text-[10px]">
              {cap.jurisdictionCode}
            </Badge>
          </div>
          <Switch
            checked={cap.isActive}
            onCheckedChange={() => onToggle?.(cap)}
            className="scale-75"
          />
        </div>

        {/* Key metric */}
        <div className="text-lg font-semibold text-slate-900 dark:text-white">
          {cap.maxAttempts} attempt{cap.maxAttempts !== 1 ? 's' : ''}{' '}
          <span className="text-sm font-normal text-slate-500 dark:text-slate-400">
            / {formatPeriod(cap.periodHours)}
          </span>
        </div>

        {/* Badges */}
        <div className="flex items-center gap-2 flex-wrap">
          {cap.channel ? (
            <Badge variant="secondary" className="text-[10px]">
              {cap.channel}
            </Badge>
          ) : (
            <Badge variant="secondary" className="text-[10px]">
              All Channels
            </Badge>
          )}
          {cap.messagePurpose && (
            <Badge
              variant={cap.messagePurpose === 'MARKETING' ? 'info' : 'success'}
              className="text-[10px]"
            >
              {cap.messagePurpose}
            </Badge>
          )}
          {cap.crossChannel && (
            <Badge variant="warning" className="text-[10px]">
              Cross-Channel
            </Badge>
          )}
        </div>

        {/* Source ref */}
        {cap.sourceRef && (
          <div className="flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400">
            <BookOpen className="w-3 h-3" />
            <span>{cap.sourceRef}</span>
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100 dark:border-slate-800">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onEdit(cap)}
            className="h-7 text-xs"
          >
            <Pencil className="w-3 h-3 mr-1" />
            Edit
          </Button>
          {onDelete && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onDelete(cap)}
              className="h-7 text-xs text-red-600 hover:text-red-700"
            >
              <Trash2 className="w-3 h-3 mr-1" />
              Delete
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
